import React, { useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { interactiveHoverVariants, modalVariants, overlayVariants } from '@/animations';
import { Button } from '@/components';
import LazyImage from '@/components/LazyImage';
import { formatCurrency, parsePrice } from '@/config';
import { PRODUCT_DETAILS_CONTENT } from '@/content';
import { useCartStore } from '@/store/cartStore';
import { useWishlistStore } from '@/store/wishlistStore';
import type { Product } from '@/types';
import { Icon } from '@/utils/icons';

import RatingStars from './RatingStars';

interface QuickViewModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
}

const QuickViewModal: React.FC<QuickViewModalProps> = ({ product, isOpen, onClose }) => {
  const addToCart = useCartStore(state => state.addToCart);
  const addToWishlist = useWishlistStore(state => state.addToWishlist);
  const removeFromWishlist = useWishlistStore(state => state.removeFromWishlist);
  const isInWishlist = useWishlistStore(state => state.isInWishlist);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(product.sizes?.[0] || '');
  const [selectedColor, setSelectedColor] = useState(product.colors?.[0] || '');
  const [mainImage, setMainImage] = useState(0);

  const isFavorite = isInWishlist(product.id);

  const handleAddToCart = () => {
    void addToCart({
      ...product,
      quantity,
      selectedSize: selectedSize || undefined,
      selectedColor: selectedColor || undefined,
    });
    onClose();
  };

  const toggleWishlist = () => {
    if (isFavorite) {
      void removeFromWishlist(product.id);
    } else {
      void addToWishlist(product);
    }
  };

  const discount = product.originalPrice
    ? Math.round((1 - parsePrice(product.price) / parsePrice(product.originalPrice)) * 100)
    : 0;

  const images = product.images?.length ? product.images : [product.thumbnail];
  const currentImage = images[mainImage] || product.thumbnail;

  const prevFocusRef = useRef<HTMLElement | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    prevFocusRef.current = document.activeElement as HTMLElement;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    setTimeout(() => modalRef.current?.focus(), 50);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();

      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      prevFocusRef.current?.focus();
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          ref={modalRef}
          tabIndex={-1}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 outline-none"
          role="dialog"
          aria-modal="true"
          aria-label={`Quick view: ${product.name}`}>
          <motion.div
            key="quickview-overlay"
            variants={overlayVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            key="quickview-modal"
            variants={modalVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="relative mx-auto flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl ring-1 ring-neutral-100 md:flex-row dark:bg-neutral-950 dark:ring-neutral-800"
            onClick={e => e.stopPropagation()}>
            <button
              onClick={onClose}
              type="button"
              className="hover:bg-primary-50 hover:text-primary-500 absolute top-4 right-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-neutral-400 shadow-lg backdrop-blur-sm transition-all dark:bg-neutral-900/90 dark:text-neutral-500"
              aria-label="Close quick view">
              <Icon name="X" size={20} />
            </button>

            <div className="relative flex w-full flex-col md:w-1/2">
              <div className="from-primary-50/30 to-primary-100/20 relative flex h-72 flex-shrink-0 items-center justify-center overflow-hidden bg-gradient-to-br md:h-full dark:from-neutral-900/50 dark:to-neutral-950">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={mainImage}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                    className="flex h-full w-full items-center justify-center p-6">
                    <LazyImage
                      src={currentImage}
                      alt={product.name}
                      priority={true}
                      className="h-full w-full"
                      imgClassName="object-contain max-h-full max-w-full drop-shadow-xl"
                    />
                  </motion.div>
                </AnimatePresence>

                <div className="bg-primary-200/20 absolute top-8 left-8 h-24 w-24 rounded-full blur-2xl" />
                <div className="bg-primary-100/10 absolute right-12 bottom-12 h-32 w-32 rounded-full blur-3xl" />

                {images.length > 1 && (
                  <div className="absolute right-2 bottom-4 left-2 flex justify-center gap-2">
                    {images.map((img, idx) => (
                      <button
                        key={img}
                        onClick={() => setMainImage(idx)}
                        type="button"
                        className={`h-2 rounded-full transition-all ${
                          idx === mainImage
                            ? 'bg-primary-500 w-6'
                            : 'w-2 bg-white/60 hover:bg-white/90'
                        }`}
                        aria-label={`View image ${idx + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex w-full flex-col overflow-y-auto p-4 md:w-1/2">
              <div className="flex-1 space-y-4">
                <div className="flex items-center gap-2">
                  <span className="font-body bg-primary-50 text-primary-600 dark:bg-primary-950/40 dark:text-primary-400 inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold">
                    {product.category}
                  </span>
                  {product.model_number && (
                    <span className="font-body inline-flex items-center gap-1.5 rounded-xl border border-neutral-100 bg-neutral-50 px-3 py-1.5 text-xs font-bold text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900">
                      Model: {product.model_number}
                    </span>
                  )}
                  {product.isNew && (
                    <span className="rounded-xl bg-green-50 px-3 py-1.5 text-xs font-bold text-green-600 dark:bg-green-950/40 dark:text-green-400">
                      {PRODUCT_DETAILS_CONTENT.badges.new}
                    </span>
                  )}
                  {discount > 0 && (
                    <span className="rounded-xl bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600 dark:bg-red-950/40 dark:text-red-400">
                      -{discount}%
                    </span>
                  )}
                </div>

                <h3 className="font-display line-clamp-2 text-xl font-bold text-neutral-800 dark:text-neutral-100">
                  {product.name}
                </h3>

                <div className="flex items-center gap-2">
                  <RatingStars rating={product.rating} size={16} />
                  <span className="font-body text-sm text-neutral-500">
                    {product.rating} ({product.reviewCount} reviews)
                  </span>
                </div>

                <div className="flex items-baseline gap-3">
                  <span className="font-display text-primary-600 text-2xl font-bold">
                    {formatCurrency(product.price)}
                  </span>
                  {product.originalPrice && (
                    <span className="text-sm text-neutral-400 line-through">
                      {formatCurrency(product.originalPrice)}
                    </span>
                  )}
                </div>

                <p className="font-body line-clamp-3 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">
                  {product.description}
                </p>

                {product.colors && product.colors.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-display text-sm font-bold text-neutral-700 dark:text-neutral-300">
                      Color
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {product.colors.map(color => (
                        <button
                          key={color}
                          onClick={() => setSelectedColor(color)}
                          type="button"
                          className={`rounded-xl px-4 py-2 text-xs font-bold transition-all ${
                            selectedColor === color
                              ? 'bg-primary-500 text-white shadow-md'
                              : 'hover:border-primary-200 border border-neutral-200 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800'
                          }`}>
                          {color}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {product.sizes && product.sizes.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-display text-sm font-bold text-neutral-700 dark:text-neutral-300">
                      Size
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {product.sizes.map(size => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          type="button"
                          className={`h-10 w-12 rounded-xl text-sm font-bold transition-all ${
                            selectedSize === size
                              ? 'bg-primary-500 text-white shadow-md'
                              : 'hover:border-primary-200 border border-neutral-200 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800'
                          }`}>
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 space-y-3 border-t border-neutral-100 pt-4 dark:border-neutral-800">
                <div className="flex items-center gap-3">
                  <div className="flex items-center rounded-2xl border border-neutral-100 bg-neutral-50 p-1 dark:border-neutral-800 dark:bg-neutral-900">
                    <motion.button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      whileTap={{ scale: 0.9 }}
                      className="hover:text-primary-600 flex h-10 w-10 items-center justify-center rounded-xl text-neutral-400 transition-colors hover:bg-white dark:hover:bg-neutral-800">
                      <Icon name="Minus" size={16} />
                    </motion.button>
                    <span className="font-display flex h-10 w-10 items-center justify-center text-sm font-bold text-neutral-800 dark:text-neutral-200">
                      {quantity}
                    </span>
                    <motion.button
                      onClick={() => setQuantity(quantity + 1)}
                      whileTap={{ scale: 0.9 }}
                      className="hover:text-primary-600 flex h-10 w-10 items-center justify-center rounded-xl text-neutral-400 transition-colors hover:bg-white dark:hover:bg-neutral-800">
                      <Icon name="Plus" size={16} />
                    </motion.button>
                  </div>

                  <Button
                    onClick={handleAddToCart}
                    disabled={!product.inStock}
                    variant="primary"
                    size="md"
                    leftIcon={<Icon name="ShoppingBag" size={18} />}
                    className="flex-1 shadow-lg">
                    {product.inStock
                      ? PRODUCT_DETAILS_CONTENT.actions.addToCart
                      : PRODUCT_DETAILS_CONTENT.actions.outOfStock}
                  </Button>

                  <motion.button
                    onClick={toggleWishlist}
                    whileHover="hover"
                    whileTap="tap"
                    variants={interactiveHoverVariants}
                    className={`flex h-10 w-10 items-center justify-center rounded-2xl shadow-md transition-all ${
                      isFavorite
                        ? 'bg-primary-500 text-white'
                        : 'hover:text-primary-500 border border-neutral-100 bg-white text-neutral-400 dark:border-neutral-800 dark:bg-neutral-900'
                    }`}
                    aria-label={isFavorite ? 'Remove from wishlist' : 'Add to wishlist'}>
                    <Icon name="Heart" size={18} fill={isFavorite ? 'currentColor' : 'none'} />
                  </motion.button>
                </div>

                <a
                  href={`/product/${product.id}`}
                  className="font-body hover:text-primary-600 block text-center text-xs font-bold text-neutral-500 underline underline-offset-4 transition-colors">
                  View full product details
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default React.memo(QuickViewModal);
